import { userQueryKeys } from '@/config/queryKeys';
import { canManageSuperAdminRole } from '@/config/users';
import { adminRoles, mainIconSize } from '@/constants.ts';
import { checkAndRedirectForOrgAdminAccess } from '@/helpers';
import { useAgGridDatasourceRefresh, useRoles, useRouteProtection } from '@/hooks';
import { useAuthStatus } from '@/hooks/useAuthStatus';
import { getDateAsString } from '@/lib';
import { apiClient } from '@/lib/api-client';
import { ROLE_HIERARCHY } from '@/lib/roles';
import { useOrganizations } from '@/pages/Organizations/hooks/useOrganizations';
import { useAppStore } from '@/stores/app';
import { useUnauthorizedReferrerStore } from '@/stores/unauthorizedReferrerStore';
import type { Role, UserPublic } from '@/types';
import { SUPER_ADMIN_ROLE } from '@/types';
import { isForbiddenError } from '@/types/errors';
import { flushColumnState, sizeColumnsOnFirstVisit } from '@/utils/flushColumnState';
import ErrorMessage from '@components/ErrorMessage';
import { FullLoading } from '@components/Loaders';
import { Avatar } from '@nautilusbio/nb-design-system/Avatar';
import Badge from '@nautilusbio/nb-design-system/Badge';
import Button from '@nautilusbio/nb-design-system/Button';
import { getTextClassName } from '@nautilusbio/nb-design-system/helpers';
import { Stack } from '@nautilusbio/nb-design-system/Stack';
import { Table } from '@nautilusbio/nb-design-system/Table';
import { Text } from '@nautilusbio/nb-design-system/Text';
import { toast } from '@nautilusbio/nb-design-system/Toaster';
import { Tooltip } from '@nautilusbio/nb-design-system/Tooltip';
import { useQuery } from '@tanstack/react-query';
import { createFileRoute, Link, redirect, useNavigate } from '@tanstack/react-router';
import type { ColDef } from 'ag-grid-community';
import type { AgGridReact } from 'ag-grid-react';
import { CloudDownload, RefreshCw } from 'lucide-react';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import OrgCell from '../../pages/Users/OrgCell';
import pageStyles from '../../pages/Users/styles.module.css';
import SuperAdminGlobalTable from '../../pages/Users/SuperAdminGlobalTable';
import SuperAdminToggleCell from '../../pages/Users/SuperAdminToggleCell';
import tableStyles from '../../styles/components/table.module.css';

/** UserPublic extended with a flat `roles` array for the selected org context */
type UserWithRoles = UserPublic & {
  roles: Role[];
  /** TODO: wire to backend permission field when available on UserPublic */
  can_create_studies?: boolean | null;
  /** TODO: wire to backend permission field when available on UserPublic */
  can_create_runs?: boolean | null;
};

export const Route = createFileRoute('/users/')({
  beforeLoad: ({ context, location }) => {
    const store = useAppStore.getState();
    const selectedOrganization = store.selectedOrganization || store.selectedOrgStub;
    // Visible to SuperAdmin (global search) and Org Admin (own-org list, GET /orgs/{id}/users is
    // org-scoped, not SuperAdmin-only — POR-397); same set as /instruments and /organizations.
    checkAndRedirectForOrgAdminAccess({
      context,
      location,
      selectedOrganization,
      adminRoles,
      redirect,
    });
  },
  component: RouteComponent,
});

function useOrgUsers(orgId: string, enabled: boolean) {
  return useQuery<UserWithRoles[]>({
    queryKey: userQueryKeys.orgUsers(orgId),
    enabled,
    queryFn: async () => {
      const res = await apiClient.get<UserPublic[]>(`/orgs/${orgId}/users`);
      return (res ?? []).map(user => ({
        ...user,
        roles: user.allowed_roles?.[orgId] ?? [],
      }));
    },
    staleTime: 5 * 60 * 1000, // 5 min
    retry: (failureCount, error) => {
      const isUnauthorized =
        ('isUnauthorized' in error && error.isUnauthorized === true) ||
        error.name === 'UnauthorizedError' ||
        // 401 (session expired) and 403 are both terminal — don't retry
        ('status' in error &&
          (error.status === 401 || error.status === 403 || error.status === '401' || error.status === '403'));
      if (isUnauthorized) return false;
      return failureCount < 3;
    },
  });
}

const defaultColDef = {
  resizable: false,
};

function RouteComponent() {
  // useRouteProtection handles org switch/deselect; effect below catches in-page role downgrade.
  useRouteProtection(adminRoles);

  const ORG_ID = useAppStore(state => state.selectedOrganization?.id ?? state.selectedOrgStub?.id);
  const { user: currentUser } = useAuthStatus();

  const { t: tGeneral } = useTranslation('general');
  const { t: tUsers } = useTranslation('users');
  const navigate = useNavigate();

  const { data: orgs = [] } = useOrganizations();
  const orgNameMap = useMemo(() => new Map(orgs.map(o => [o.id, o.name])), [orgs]);

  // isGlobalSuperAdmin: platform flag + assumed role must both be SuperAdmin.
  const { isSuperAdmin, getCurrentRole } = useRoles();
  const isGlobalSuperAdmin = canManageSuperAdminRole(currentUser) && !!ORG_ID && isSuperAdmin(ORG_ID);
  const assumedRole = ORG_ID ? getCurrentRole(ORG_ID) : undefined;

  // Redirect immediately on in-page role downgrade.
  useEffect(() => {
    if (!assumedRole) return;
    if (!(adminRoles as string[]).includes(assumedRole)) {
      useUnauthorizedReferrerStore.getState().setReferrerPath(window.location.pathname);
      void navigate({ to: '/unauthorized' });
    }
  }, [assumedRole, navigate]);

  // Disabled for global SuperAdmins (they use the server-side table); avoids malformed /orgs//users request
  const { data: users, isLoading, error, refetch } = useOrgUsers(ORG_ID ?? '', !isGlobalSuperAdmin && !!ORG_ID);

  // Ref for the global SuperAdmin AG Grid instance
  const superAdminGridRef = useRef<AgGridReact<UserPublic> | null>(null);

  // Total count reported back from AG Grid pagination (SA view only)
  const [saTotalCount, setSaTotalCount] = useState(0);
  const saRefresh = useAgGridDatasourceRefresh(userQueryKeys.endpoints.globalSearch(), superAdminGridRef);

  useEffect(() => {
    // Only 403 should land on /unauthorized. 401 is handled globally by
    // ApiClient, which navigates to /login after clearing client state.
    if (error && isForbiddenError(error)) {
      void navigate({ to: '/unauthorized' });
    }
  }, [error, navigate]);

  const columns: ColDef<UserWithRoles>[] = useMemo(
    () => [
      {
        cellRenderer: ({ data }: { data: UserWithRoles }) => {
          const nameText = (
            <Text as="span" className={getTextClassName('sm')}>
              {data.full_name}
            </Text>
          );
          return (
            <Stack alignItems="center" gap={4} orientation="horizontal">
              <Avatar size="xs2">{data.full_name}</Avatar>
              {
                // /users/$userId is still SuperAdmin-only (its own separate, unresolved gap) —
                // don't link Org Admins into a route that dead-ends every time.
                assumedRole === SUPER_ADMIN_ROLE ? (
                  <Link params={{ userId: data.id }} to="/users/$userId">
                    {nameText}
                  </Link>
                ) : (
                  nameText
                )
              }
            </Stack>
          );
        },
        field: 'full_name',
        headerName: tGeneral('name'),
        maxWidth: 240,
      },
      { field: 'email', headerName: tGeneral('email') },
      {
        // TODO: wire to backend permission field when available on UserPublic
        field: 'can_create_studies',
        headerName: tUsers('can_create_studies_column_header'),
        filter: false,
        sortable: false,
        valueFormatter: ({ value }) =>
          value !== null && value !== undefined ? (value ? tGeneral('yes') : tGeneral('no')) : '—',
        maxWidth: 140,
      },
      {
        // TODO: wire to backend permission field when available on UserPublic
        field: 'can_create_runs',
        headerName: tUsers('can_create_runs_column_header'),
        filter: false,
        sortable: false,
        valueFormatter: ({ value }) =>
          value !== null && value !== undefined ? (value ? tGeneral('yes') : tGeneral('no')) : '—',
        maxWidth: 120,
      },
      {
        cellClass: pageStyles.rolesCell,
        cellRenderer: ({ value }: { value: Role[] }) => {
          // Filter out SuperAdmin and sort the rest by hierarchy
          const filteredRoles = value.filter(role => role !== ROLE_HIERARCHY[0]);
          const sortedRoles = [...filteredRoles].toSorted(
            (a, b) => ROLE_HIERARCHY.indexOf(a) - ROLE_HIERARCHY.indexOf(b),
          );

          return (
            <Stack alignItems="center" fullWidth gap={2} orientation="horizontal">
              {sortedRoles.map(role => (
                <Badge className={pageStyles.badge} key={role} size="xs" variant="default">
                  {role}
                </Badge>
              ))}
            </Stack>
          );
        },
        field: 'roles',
        headerName: tGeneral('roles'),
      },
      {
        // Org membership resolved from allowed_roles keys; multiple orgs shown via Tooltip
        colId: 'organization',
        headerName: tUsers('organization_column_header'),
        hide: !isGlobalSuperAdmin,
        filter: false,
        sortable: false,
        cellRenderer: ({ data }: { data: UserWithRoles }) => (
          <OrgCell orgIds={Object.keys(data.allowed_roles ?? {})} orgNameMap={orgNameMap} />
        ),
      },
      {
        field: 'last_login_at',
        headerName: tUsers('last_login_at_column_header'),
        valueFormatter: ({ value }) => (value ? getDateAsString(new Date(value)) : '—'),
        maxWidth: 180,
      },
      {
        field: 'updated_at',
        headerName: tGeneral('updated_at'),
        valueFormatter: ({ value }) => (value ? getDateAsString(new Date(value)) : '—'),
        maxWidth: 180,
      },
      {
        field: 'updated_by_full_name',
        headerName: tGeneral('updated_by'),
        hide: true,
        valueFormatter: ({ value }) => value ?? '—',
        maxWidth: 200,
      },
      {
        field: 'created_at',
        headerName: tGeneral('created_at'),
        valueFormatter: ({ value }) => (value ? getDateAsString(new Date(value)) : '—'),
        maxWidth: 180,
      },
      {
        field: 'created_by_full_name',
        headerName: tGeneral('created_by'),
        hide: true,
        valueFormatter: ({ value }) => value ?? '—',
        maxWidth: 200,
      },
      // SuperAdmin toggle column — only rendered for global SuperAdmins
      ...(isGlobalSuperAdmin && currentUser
        ? [
            {
              cellClass: tableStyles.tableCellCenter,
              cellRenderer: ({ data }: { data: UserWithRoles }) => (
                <SuperAdminToggleCell currentUserId={currentUser.id} orgId={ORG_ID as string} user={data} />
              ),
              colId: 'super_admin_toggle',
              field: 'roles' as const,
              filter: false,
              width: 80,
              headerName: tUsers('super_admin_column_header'),
              sortable: false,
            },
          ]
        : []),
    ],
    [tGeneral, tUsers, isGlobalSuperAdmin, currentUser, ORG_ID, orgNameMap, assumedRole],
  );

  // Promise-based refetch handler with toast feedback.
  const handleRefetch = useCallback(async () => {
    const toastId = toast.loading(tUsers('refetching'));
    try {
      // refetch() resolves (never rejects by default) even on a failed request, so the
      // result's own `error` field — not a catch block — is what a failure looks like here.
      const result = await refetch();
      if (result.error) throw result.error;
      toast.success(tUsers('refetch_success'), { id: toastId });
    } catch (err) {
      toast.error(tUsers('refetch_error') + (err instanceof Error && err.message ? `: ${err.message}` : ''), {
        id: toastId,
      });
    }
  }, [refetch, tUsers]);

  // Show loading only for non-SuperAdmin org-scoped query
  if (!isGlobalSuperAdmin && isLoading) return <FullLoading label={tUsers('loading')} />;

  if (!isGlobalSuperAdmin && error) return <ErrorMessage error={error} />;

  // ── Global SuperAdmin view: server-side table with POST /users/search ──
  if (isGlobalSuperAdmin && currentUser && ORG_ID) {
    return (
      <Stack alignItems="start" className={pageStyles.pageContainer} orientation="vertical">
        <Stack
          alignItems="center"
          className={pageStyles.newTitleRow}
          justifyContent="space-between"
          orientation="horizontal"
        >
          <Stack alignItems="start" justifyContent="start" orientation="vertical">
            <Text as="h1" className={pageStyles.title}>
              {tUsers('title')}
            </Text>
            {saTotalCount > 0 && (
              <Text as="small" className={pageStyles.subtitle}>
                {tUsers('user_count', { count: saTotalCount })}
              </Text>
            )}
          </Stack>

          <Stack alignItems="center" gap={2} orientation="horizontal">
            <Tooltip
              content={tUsers('refetch_tooltip')}
              placement="bottom-end"
              trigger={
                <Button
                  aria-label={tUsers('refetch_tooltip')}
                  data-testid="sa-refetch-users-btn"
                  onPress={saRefresh}
                  size="md"
                  visual="unstyled"
                >
                  <RefreshCw size={mainIconSize} />
                </Button>
              }
            />
            <Tooltip
              content={tUsers('download_list_coming_soon')}
              placement="bottom"
              trigger={
                <Button data-testid="download-list-btn" isDisabled size="md" visual="unstyled">
                  <CloudDownload size={mainIconSize} />
                  {tUsers('download_list')}
                </Button>
              }
            />
          </Stack>
        </Stack>

        <SuperAdminGlobalTable
          currentUserId={currentUser.id}
          gridRef={superAdminGridRef}
          onMutationSettled={saRefresh}
          onTotalCountChange={setSaTotalCount}
          orgId={ORG_ID}
          orgNameMap={orgNameMap}
        />
      </Stack>
    );
  }

  // ── Standard org-scoped view ──
  return (
    <Stack alignItems="start" className={pageStyles.pageContainer} orientation="vertical">
      <Stack
        alignItems="center"
        className={pageStyles.newTitleRow}
        justifyContent="space-between"
        orientation="horizontal"
      >
        <Stack alignItems="start" justifyContent="start" orientation="vertical">
          <Text as="h1" className={pageStyles.title}>
            {tUsers('title')}
          </Text>
          <Text as="small" className={pageStyles.subtitle}>
            {tUsers('user_count', { count: users?.length ?? 0 })}
          </Text>
        </Stack>

        <Stack alignItems="center" gap={2} orientation="horizontal">
          <Tooltip
            content={tUsers('refetch_tooltip')}
            placement="bottom-end"
            trigger={
              <Button
                aria-label={tUsers('refetch_tooltip')}
                data-testid="refetch-users-btn"
                onPress={handleRefetch}
                size="md"
                visual="unstyled"
              >
                <RefreshCw size={mainIconSize} />
              </Button>
            }
          />
          <Tooltip
            content={tUsers('download_list_coming_soon')}
            placement="bottom"
            trigger={
              <Button data-testid="download-list-btn" isDisabled size="md" visual="unstyled">
                <CloudDownload size={mainIconSize} />
                {tUsers('download_list')}
              </Button>
            }
          />
        </Stack>
      </Stack>

      <Table
        className={tableStyles.smallTable}
        container={{ className: pageStyles.tableContainer }}
        columnDefs={columns}
        defaultColDef={defaultColDef}
        density="loose"
        enableResponsiveResize={false}
        pagination
        paginationPageSize={25}
        paginationPageSizeSelector={[25, 50, 100]}
        resizeDebounceMs={150}
        rowData={users ?? []}
        tableId="users-table"
        onGridReady={e => sizeColumnsOnFirstVisit(e.api, 'users-table')}
        onGridPreDestroyed={e => flushColumnState(e.api, 'users-table')}
      />
    </Stack>
  );
}
